import { useEffect } from 'react';
import { pickLocale, type AreaCharacteristic } from '../../schema';
import { useCharacteristicSelection, useStoreActions } from '../../state/StoreContext';
import { formatBound } from '../../i18n/formatBound';
import { useTranslation } from '../../i18n';

interface AreaViewProps {
    characteristic: AreaCharacteristic;
    locale: string | undefined;
}

export function AreaView({ characteristic, locale }: AreaViewProps) {
    const { t } = useTranslation();
    const selection = useCharacteristicSelection(characteristic.id);
    const { setText } = useStoreActions();
    const defaultText = pickLocale(characteristic.defaultValue, locale) ?? '';
    // Legacy `placeholder: "1"` means: show defaultValue as the HTML
    // placeholder and start the textarea empty. Mirrors storage.js:439-445.
    const useAsPlaceholder = characteristic.useDefaultAsPlaceholder && defaultText !== '';
    const initialValue = useAsPlaceholder ? '' : defaultText;
    const current = selection?.kind === 'text' ? selection.value : initialValue;

    // Seed the store from the view when a non-empty defaultValue is
    // rendered as the initial value. See TextView for the full rationale —
    // without this the required-validator sees no selection and reports
    // "Pflichtfeld" even though the textarea visibly shows the default.
    useEffect(() => {
        if (selection === undefined && initialValue !== '') {
            setText(characteristic.id, initialValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [characteristic.id]);

    const maxLen = characteristic.maxStringLength;
    const maxLenNum = maxLen ? Number(maxLen) : undefined;
    const minLen = characteristic.minStringLength;
    const minLenNum = minLen ? Number(minLen) : undefined;

    return (
        <>
            <textarea
                className="cfg-textarea"
                value={current}
                placeholder={useAsPlaceholder ? defaultText : ''}
                required={characteristic.isRequired}
                minLength={
                    Number.isFinite(minLenNum) && minLenNum && minLenNum > 0
                        ? minLenNum
                        : undefined
                }
                maxLength={
                    Number.isFinite(maxLenNum) && maxLenNum && maxLenNum > 0
                        ? maxLenNum
                        : undefined
                }
                onChange={(e) => setText(characteristic.id, e.target.value)}
            />
            {maxLen && <p className="cfg-char__info">{t('area.max_chars', { value: formatBound(maxLen, locale) })}</p>}
        </>
    );
}
